import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalLayout } from "@/components/legal-layout";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/about")({
  component: AboutPage,
});

function AboutPage() {
  return (
    <LegalLayout title="About Images AI" lastUpdated="October 3, 2026">
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-4">Our Mission</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Images AI empowers creators, businesses, and individuals to bring
            their ideas to life through cutting-edge artificial intelligence. We
            believe that everyone should have access to professional-grade image
            generation and editing tools, regardless of technical expertise or
            artistic background.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Our mission is to democratize creative expression by making
            AI-powered image generation fast, accessible, and affordable.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">What We Do</h2>
          <p className="text-muted-foreground leading-relaxed mb-6">
            Images AI is a platform for AI-powered image generation and editing.
            Our services enable you to:
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-3">
                🎨 Generate from Text
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Transform any written description into stunning images using
                state-of-the-art AI models. From concepts to completed visuals
                in seconds.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-3">✏️ Edit with AI</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Modify existing images with natural language instructions,
                powered by Qwen Image 3. Change styles, add elements, or
                transform photos without traditional editing tools.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-3">☁️ Cloud Storage</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                All your generated images are securely stored and accessible
                from anywhere. Browse your creation history and track edits over
                time.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-3">⚡ Lightning Fast</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Generate professional-quality images in seconds with curated flagship models.
                No waiting in queues or long rendering times.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">Our Technology</h2>
          <p className="text-muted-foreground leading-relaxed mb-6">
            We leverage the latest advancements in artificial intelligence and
            cloud infrastructure to deliver exceptional results:
          </p>

          <div className="space-y-4">
            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-2">Advanced AI Models</h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-2">
                We use state-of-the-art AI models for both image generation and
                editing, combining speed with quality to produce high-resolution
                images from text prompts and natural language editing
                instructions.
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Powered by:</strong> FLUX 3, GPT Image 2.5, Qwen Image 3, Seedream 5, Ideogram V4, Nano Banana Pro, Recraft V4.1, and Riverflow 2.5
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-2">
                Cloud Infrastructure
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-2">
                Built on a global edge network for maximum speed, reliability,
                and security. Your data is protected with enterprise-grade
                encryption and distributed storage.
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Infrastructure:</strong> Cloudflare Workers, D1
                Database, R2 Storage
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-2">Secure Payments</h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-2">
                Payment processing handled by our secure payment partner, ensuring your financial
                information is always secure and never stored on our servers.
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Compliance:</strong> GDPR
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">Commitment to Quality</h2>
          <p className="text-muted-foreground leading-relaxed mb-6">
            We are committed to providing the best possible experience:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-3 ml-4">
            <li>
              <strong>Continuous Improvement:</strong> We regularly update our
              AI models and features based on user feedback and technological
              advances
            </li>
            <li>
              <strong>Privacy First:</strong> Your data is protected with
              GDPR-compliant privacy practices and enterprise-grade security
            </li>
            <li>
              <strong>Transparent AI:</strong> We clearly disclose AI usage in
              accordance with the EU AI Act and provide full ownership of your
              generated content
            </li>
            <li>
              <strong>Responsible Development:</strong> We implement safeguards
              against misuse and actively moderate content to maintain a safe
              platform
            </li>
            <li>
              <strong>Customer Support:</strong> Our team is here to help with
              technical issues, billing questions, and general inquiries
            </li>
          </ul>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">Pricing & Plans</h2>
          <p className="text-muted-foreground leading-relaxed mb-6">
            We offer flexible pricing to suit different needs:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-muted/50 border border-border rounded-2xl p-6 text-center">
              <h3 className="text-xl font-semibold mb-2">Free</h3>
              <p className="text-3xl font-bold mb-4">
                5{" "}
                <span className="text-base font-normal text-muted-foreground">
                  credits (one-time)
                </span>
              </p>
              <p className="text-sm text-muted-foreground">
                Perfect for trying out the platform
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6 text-center">
              <h3 className="text-xl font-semibold mb-2">Pro</h3>
              <p className="text-3xl font-bold mb-4">
                40{" "}
                <span className="text-base font-normal text-muted-foreground">
                  credits/month
                </span>
              </p>
              <p className="text-sm text-muted-foreground">
                For regular creators and hobbyists
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6 text-center">
              <h3 className="text-xl font-semibold mb-2">Premium</h3>
              <p className="text-3xl font-bold mb-4">
                120{" "}
                <span className="text-base font-normal text-muted-foreground">
                  credits/month
                </span>
              </p>
              <p className="text-sm text-muted-foreground">
                For professionals and businesses
              </p>
            </div>
          </div>
          <div className="text-center mt-8">
            <Link to="/signup">
              <Button size="lg" className="rounded-full px-8">
                Get Started Free
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            Service Provider Information
          </h2>
          <div className="bg-muted/50 border border-border rounded-2xl p-6">
            <p className="mb-2">
              <strong>Operated by:</strong> Harshil Agrawal
            </p>
            <p className="mb-2">
              <strong>Location:</strong> Berlin, Germany
            </p>
            <p className="mt-6 text-sm text-muted-foreground">
              For complete legal information, please visit our{" "}
              <Link to="/impressum" className="text-foreground hover:underline">
                Impressum
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">Get in Touch</h2>
          <p className="text-muted-foreground leading-relaxed mb-6">
            We'd love to hear from you! Whether you have questions, feedback, or
            just want to say hello, our team is here to help.
          </p>
          <div className="flex gap-4">
            <Link to="/contact">
              <Button variant="outline" className="rounded-full">
                Contact Us
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="rounded-full">Start Creating</Button>
            </Link>
          </div>
        </div>
      </section>
    </LegalLayout>
  );
}
